import React from "react";
import { capitalize } from "lodash";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { getDeviceInfo } from "../../../../commons/libs/browser";
import DeviceInformation from "../../../commons/components/DeviceInformation/DeviceInformation";
import Headline from "../../../commons/components/Headline/Headline";
import InformationList from "../../../commons/components/InformationList/InformationList";
import Text from "../../../commons/components/Text/Text";
import useEnv from "../../../commons/libs/queries/use-env";
import { hasAdminDebugCookie } from "../../libs/cookies";
import { getStoredAuthenticationCredentials } from "../../libs/credentials";

type Props = {
  informationListVariant?: React.ComponentProps<typeof InformationList>["variant"];
  errorLog?: React.ReactNode;
  timestamp?: moment.Moment;
};

const DeviceInformationPartial = ({ errorLog, timestamp, informationListVariant = "primary" }: Props) => {
  const { t } = useTranslation(["mobile"]);
  const env = useEnv();

  const storedAuthenticationCredentials = getStoredAuthenticationCredentials();

  // Device information can be rendered when an error is set before initialization is done.
  // Using `query.data` allows access before env is loaded (will be undefined).
  // Falls back to stored credentials if env is not available.
  const serviceTag = env.query.data?.serviceTag ?? storedAuthenticationCredentials.serviceTag;

  const deviceInfo = getDeviceInfo(navigator.userAgent);

  const isAdminDebug = hasAdminDebugCookie();

  return (
    <DeviceInformation>
      <Text>
        <Headline>{t("mobile:deviceInformationPartialApp.deviceInformationHeadline")}</Headline>
        <InformationList
          variant={informationListVariant}
          data={{
            ServiceTag: serviceTag ?? "",
            ["Device"]: `${deviceInfo.device.vendor} ${deviceInfo.device.model} (${capitalize(deviceInfo.device.type)})`,
            ["Browser"]: `${deviceInfo.browser.name} ${deviceInfo.browser.version}`,
            ["OS"]: `${deviceInfo.os.name} ${deviceInfo.os.version}`,
            ["Viewport"]: `${window.innerWidth}x${window.innerHeight}px`,
            ["Timestamp"]: timestamp ? timestamp.toString() : moment().toString(),
            ["License"]: isAdminDebug
              ? "Admin Debug (no license consumed)"
              : `${storedAuthenticationCredentials.licenseId} ${storedAuthenticationCredentials.licenseToken?.slice(0, 8)}`
          }}
        />
      </Text>
      {errorLog}
    </DeviceInformation>
  );
};

export default DeviceInformationPartial;
